// Componentes nativos
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Label } from "recharts";
import Modal from "react-modal";

// Componentes personalizados
import Header from "../../Header";

// Ícones
import { IoMdArrowRoundBack } from "react-icons/io";
import { BsFillGearFill } from "react-icons/bs";

// Classes de controle e de armazenamento
import OffStorage, { DadosAntoine } from "../Core/OffStorage";
import { Antoine } from "../Core/PressaoSaturacao";

// Tipagem intermediária para o gráfico
type DataGraphSaturacao = {
    "Temperatura": number,
    "Pressão": number
};

export default function DiagramaSaturacao() {
    // Gerencia a navegação
    const nav = useHistory();
    function back() {
        nav.goBack();
    }

    // Cria a instância do banco offline
    const banco = new OffStorage();

    // Gerencia as substâncias cadastradas e selecionada
    const [substancias, setSubstancias] = useState<DadosAntoine[]>([]);
    const [selecionada, setSelecionada] = useState("");
    const [temperaturaMinima, setTemperaturaMinima] = useState("0");
    const [temperaturaMaxima, setTemperaturaMaxima] = useState("100");

    // Gerencia o alerta dos inputs
    const [alerta, setAlerta] = useState("");

    // Gerencia o carregamento do gráfico
    const [carregado, setCarregado] = useState(false);
    const [dataGraph, setDataGraph] = useState<DataGraphSaturacao[]>([]);

    // Função para cálculo dos valores a serem utilizados no gráfico
    function calcular() {
        const dados = buscar(selecionada);
        const antoine = new Antoine(String(dados.substancia?.nome), String(dados.substancia?.formula));
        antoine.setConstantes(dados.A, dados.B, dados.C);

        // Define o passo e o delta, de acordo com o intervalo
        const intervalo = parseFloat(temperaturaMaxima) - parseFloat(temperaturaMinima);
        let divisor = 100;
        let passo = intervalo / divisor;
        while(passo > 0.01 * intervalo) {
            divisor *= 1.1;
            passo = intervalo / divisor;
        }

        // Faz o cálculo para cada ponto
        const data: DataGraphSaturacao[] = [];
        for(let i = 0; i <= divisor; i++) {
            let temperatura = parseFloat(temperaturaMinima) + i * passo;
            let pressao = antoine.calcularPressao(temperatura);

            data.push({
                "Pressão": pressao,
                "Temperatura": temperatura
            });
        }

        // Atualiza variáveis de estado
        setDataGraph(data);
        setCarregado(true);
    }

    // Função de render do gráfico
    function render() {
        if(selecionada == "") {
            setAlerta("Atenção: Escolha uma substância para acompanhar na lista disponível.");
            setCarregado(false);
            return;
        }

        if(temperaturaMinima == "" || temperaturaMaxima == "") {
            return;
        }

        if(parseFloat(temperaturaMinima) >= parseFloat(temperaturaMaxima)) {
            setAlerta("Atenção: Por favor, escolha defina a temperatura mínima com um valor numérico menor do que a temperatura máxima.");
            setCarregado(false);
            return;
        }

        setAlerta("");
        calcular();
    }

    useEffect(() => {
        render();
    }, [selecionada, temperaturaMinima, temperaturaMaxima]);

    // Função de busca pelos dados a partir do nome
    function buscar(nome: string) {
        let dados = substancias[0];
        substancias.forEach(sub => {
            if(sub.substancia?.nome == nome) {
                dados = sub;
            }
        });

        return dados;
    }

    // Função de seleção de substância
    function selecionar(event: React.ChangeEvent<HTMLSelectElement>) {
        setSelecionada(event.target.value);
    }

    // Funções para seleção do range de temperatura
    function seltmin(event: React.ChangeEvent<HTMLInputElement>) {
        setTemperaturaMinima(event.target.value);

        const valor = parseFloat(event.target.value.replace(",", "."));
        if(isNaN(valor)) {
            setAlerta("Atenção: Informe um valor numérico válido para a temperatura mínima.");
        } else {
            setAlerta("");
            setTemperaturaMinima(valor.toString());
        }
    }

    function seltmax(event: React.ChangeEvent<HTMLInputElement>) {
        setTemperaturaMinima(event.target.value);

        const valor = parseFloat(event.target.value.replace(",", "."));
        if(isNaN(valor)) {
            setAlerta("Atenção: Informe um valor numérico válido para a temperatura máxima.");
        } else {
            setAlerta("");
            setTemperaturaMaxima(valor.toString());
        }
    }

    // Função de inicialização
    function init() {
        const disponiveis = banco.getDadosAntoine();
        setSubstancias(disponiveis);
    }

    useEffect(() => {
        init();
    }, []);

    return (
        <div className="section">
            <Header />

            <div className="content">
                <div className="container">

                    <div className="linha-titulo">
                        <h3>Pressão e Temperatura de Saturação - Diagramas</h3>

                        <div className="botao-icone" onClick={back}>
                            <IoMdArrowRoundBack size={20} />
                            <span>Voltar</span>
                        </div>
                    </div>

                    <div className="linha-nivel">
                        <span className="nivel"><strong>Nível</strong>: Avançado</span>
                    </div>

                    <p>Aqui você pode criar diagramas de Pressão ou Temperatura de Saturação, ou seja, a partir da fixação de um valor, você pode ver como o outro varia. A Equação de Antoine é utilizada para realizar os cálculos preditivos.</p>

                    <p>OBS.: A Equação de Antoine possui limites de temperatura, um intervalo em que a predição é confiável. Porém, o objetivo da ferramenta nesta página é proporcionar uma interface gráfica para análises rápidas e qualitativas. Por isso estes limites não são considerados aqui, enquanto em outros lugares eles são apresentados.</p>

                    <div className="grid-row recuo-row">

                        <div className="coluna-2 pad-r">
                            <label className="label-select-ferramenta">Seleciona a substância a ser monitorada</label>
                        </div>

                        <div className="coluna-2 pad-l">
                            <select className="select-ferramenta" onChange={selecionar}>
                                <option value="">Substâncias Cadastradas</option>
                                {substancias.map(item => (
                                    <option value={item.substancia?.nome}>{item.substancia?.nome}</option>
                                ))}
                            </select>
                        </div>

                    </div>

                    <div className="grid-row recuo-row">
                        <div className="coluna-2 pad-r">
                            <div className="form-ferramenta">
                                <label>Temperatura Mínima (°C)</label>
                                <input type="number" value={temperaturaMinima} onChange={seltmin} />
                            </div>
                        </div>

                        <div className="coluna-2 pad-l">
                            <div className="form-ferramenta">
                                <label>Temperatura Máxima (°C)</label>
                                <input type="number" value={temperaturaMaxima} onChange={seltmax} />
                            </div>
                        </div>
                    </div>

                    <div className="grid-row">
                        <p className="alerta-ferramenta">{alerta}</p>
                    </div>

                    {carregado && <div className="grafico-ferramenta">
                        <h4 className="label-titulo-grafico">Pressão de Saturação vs Temperatura</h4>

                        <ResponsiveContainer minWidth={350} height={300}>
                            <LineChart data={dataGraph} >
                                <XAxis dataKey="Temperatura" stroke="#1a1a1a">
                                    <Label value="Temperatura (°C)" offset={-8} position="bottom" fontFamily="Ubuntu" />
                                </XAxis>

                                <YAxis stroke="#1a1a1a">
                                    <Label value="Pressão de Saturação (kPa)" fontFamily="Ubuntu" angle={-90} position="center" dx={-22} />
                                </YAxis>

                                <Tooltip />
                                <Line type="monotone" dataKey="Pressão" stroke="#1a1a1a" dot={false}/>
                            </LineChart>
                        </ResponsiveContainer>
                    </div>}
                
                </div>

            </div>
        </div>
    );
}